/**
 * Creates resource params only when a customer id is available.
 *
 * Why this exists:
 * - Signal-store resources often need `customerId` in their params.
 * - Without this helper, each resource repeats the same null-check boilerplate.
 *
 * Behavior:
 * - Returns `undefined` when `customerId` is missing.
 * - Calls `build(customerId)` when `customerId` exists.
 * - Returns the builder result unchanged (including `undefined`).
 *
 * @example
 * ```ts
 * params: () =>
 *   withCustomerParams(customer()?.id, (customerId) => ({
 *     customerId,
 *     cropYear: store.cropYear(),
 *   }));
 * ```
 *
 * @example
 * ```ts
 * params: () =>
 *   withCustomerParams(customer()?.id, (customerId) => {
 *     if (!store.fromDate() || !store.toDate()) {
 *       return undefined;
 *     }
 *
 *     return {
 *       customerId,
 *       fromDate: store.fromDate(),
 *       toDate: store.toDate(),
 *     };
 *   });
 * ```
 */
export const withCustomerParams = <T>(
  customerId: number | undefined,
  build: (customerId: number) => T | undefined,
): T | undefined => {
  if (!customerId) {
    return undefined;
  }

  return build(customerId);
};
